import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createAuthenticatedEventSource, resolveAccessToken } from '../../../services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { parseExistingAIData } from '../../../utils/aiResponseParser'
import logger from '../../../utils/logger'
import { LoadingSpinner } from '../../atoms'
import { DrawerCard, DrawerEmptyState } from '../../DrawerComponents'
import ConversationModal from '../ConversationModal'
import styles from '../CallDrawerModern.module.scss'

export const ConversationCard = ({ selectedRow }) => {
  const [conversationFlow, setConversationFlow] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasChecked, setHasChecked] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const eventSourceRef = useRef(null)
  const streamEpochRef = useRef(0)

  const getCallId = useCallback(() => {
    if (selectedRow?.id) return selectedRow.id
    if (selectedRow?.call_id) return selectedRow.call_id
    if (selectedRow?.[3]) return selectedRow[3]
    return null
  }, [selectedRow])

  const generateConversationFlow = useCallback(async () => {
    const callId = getCallId()
    if (!callId) return

    /* istanbul ignore next -- cleanup of previous EventSource */
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    setIsLoading(true)

    const streamEpoch = streamEpochRef.current
    const accessToken = await resolveAccessToken()

    if (streamEpoch !== streamEpochRef.current) return

    eventSourceRef.current = createAuthenticatedEventSource(
      apiEndpoints.aiProcessCallStream,
      { callId },
      {
        onProgress: () => {},
        onComplete: (data) => {
          if (data && data.conversationFlow && Array.isArray(data.conversationFlow)) {
            setConversationFlow(data.conversationFlow)
          }
          setIsLoading(false)
        },
        onError: () => {
          setIsLoading(false)
        }
      },
      accessToken
    )
  }, [getCallId])

  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset and data check when selectedRow changes */
  useEffect(() => {
    streamEpochRef.current += 1
    /* istanbul ignore next -- EventSource ref cleanup on row change */
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setHasChecked(false)
    setConversationFlow(null)
    setIsLoading(false)
    setIsModalOpen(false)
  }, [selectedRow?.id, selectedRow?.call_id])

  useEffect(() => {
    return () => {
      streamEpochRef.current += 1
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  useEffect(() => {
    if (selectedRow && !hasChecked) {
      setHasChecked(true)

      if (selectedRow.ai_transcript) {
        try {
          const { isValid } = parseExistingAIData(selectedRow, 'ai_transcript')
          if (isValid) {
            const transcriptData = typeof selectedRow.ai_transcript === 'string'
              ? JSON.parse(selectedRow.ai_transcript)
              : selectedRow.ai_transcript
            if (transcriptData?.conversationFlow && Array.isArray(transcriptData.conversationFlow)) {
              setConversationFlow(transcriptData.conversationFlow)
              return
            }
          }
        } catch (e) {
          logger.warn('Failed to parse conversation flow', e)
        }
      }
      generateConversationFlow()
    }
  }, [selectedRow, hasChecked, generateConversationFlow])
  /* eslint-enable react-hooks/set-state-in-effect */

  const previewExchanges = conversationFlow?.slice(0, 5) || []
  const hasMoreExchanges = conversationFlow && conversationFlow.length > 5

  return (
    <>
      <DrawerCard
        title="Conversation Flow"
        icon="comments"
        collapsible
        defaultExpanded={!!selectedRow?.ai_transcript}
      >
        <Box className={styles.conversationCardInner}>
          {isLoading ? (
            <Box className={styles.loadingIndicator}>
              <LoadingSpinner size={20} message='Loading conversation...' />
            </Box>
          ) : conversationFlow && conversationFlow.length > 0 ? (
            <Box className={styles.conversationCardContent}>
              <Box className={`${styles.conversationPreview} ${hasMoreExchanges ? styles.conversationPreviewFade : ''}`}>
                {previewExchanges.map((segment, index) => (
                  <Box key={`${getCallId()}-${index}`} className={styles.conversationLine}>
                    <Box className={`${styles.speakerIcon} ${styles[segment.speaker]}`}>
                      {segment.speaker === 'agent' ? (
                        <img
                          src="/favicon.ico"
                          alt="Agent"
                          className={styles.vetaLogo}
                        />
                      ) : (
                        <FontAwesomeIcon icon="phone" />
                      )}
                    </Box>
                    <Box>
                      <Typography className={styles.speakerLabel}>
                        {segment.speaker === 'agent' ? 'Agent' : 'Caller'}
                      </Typography>
                      <Typography className={styles.conversationText}>
                        {segment.content}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {conversationFlow.length > 1 && (
                <button
                  className={styles.viewFullButton}
                  onClick={() => setIsModalOpen(true)}
                >
                  <FontAwesomeIcon icon="expand" />
                  View Full Transcript ({conversationFlow.length} exchanges)
                </button>
              )}
            </Box>
          ) : hasChecked ? (
            <DrawerEmptyState
              icon="comments"
              text="No conversation available"
              subtext="The transcript is not available for this call."
            />
          ) : null}
        </Box>
      </DrawerCard>

      <ConversationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        conversationFlow={conversationFlow}
        callId={getCallId()}
        agentName={selectedRow?.[6] || selectedRow?.agent_name}
        callerName={selectedRow?.[17] || selectedRow?.caller_name}
      />
    </>
  )
}

ConversationCard.propTypes = {
  selectedRow: PropTypes.object
}

export default ConversationCard
